import React, { useEffect, useState } from 'react'
import api from '../api'
import { Plus, Satellite, Eye, EyeOff } from 'lucide-react'
import { Card, PageHeader, Button, Input, Field, Modal, ModalHeader, Alert, Badge, Select, Table, THead, td, EmptyState } from '../components/ui'

function formDefault() {
  return { nama: '', driver: '', host: '', port: '', username: '', password: '', aktif: true }
}

export default function PerangkatOlt() {
  const [data, setData] = useState([])
  const [drivers, setDrivers] = useState([])
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(formDefault())
  const [editId, setEditId] = useState(null)
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  useEffect(() => { load(); loadDrivers() }, [])

  async function load() {
    const res = await api.get('/olt-perangkat')
    setData(res.data)
  }

  async function loadDrivers() {
    const res = await api.get('/olt-perangkat/drivers')
    setDrivers(res.data)
  }

  const driverLabel = driverKey => drivers.find(d => d.key === driverKey)?.vendor || driverKey

  function bukaTambah() {
    setForm({ ...formDefault(), driver: drivers[0]?.key || '' })
    setEditId(null); setError(''); setShowPassword(false); setModal(true)
  }

  function bukaEdit(d) {
    setForm({
      nama: d.nama, driver: d.driver, host: d.host, port: d.port || '',
      username: d.username || '', password: d.password || '', aktif: !!d.aktif
    })
    setEditId(d.id); setError(''); setShowPassword(false); setModal(true)
  }

  async function simpan() {
    if (!form.nama.trim()) { setError('Nama perangkat wajib diisi'); return }
    if (!form.driver) { setError('Driver wajib dipilih'); return }
    if (!form.host.trim()) { setError('Host/alamat wajib diisi'); return }
    try {
      if (editId) await api.put(`/olt-perangkat/${editId}`, form)
      else await api.post('/olt-perangkat', form)
      setModal(false); load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal menyimpan')
    }
  }

  async function hapus(d) {
    if (!window.confirm(`Hapus perangkat OLT "${d.nama}"? Scan redaman & diagnosis tidak akan lagi memeriksa perangkat ini.`)) return
    await api.delete(`/olt-perangkat/${d.id}`)
    load()
  }

  return (
    <div>
      <PageHeader
        title="Perangkat OLT"
        subtitle="Daftar OLT yang dipantau scan redaman otomatis & diagnosis pelanggan — tambah unit baru tanpa perlu ubah kode/env"
        actions={<Button icon={Plus} onClick={bukaTambah}>Tambah Perangkat OLT</Button>}
      />

      {data.length === 0 ? (
        <Card><EmptyState icon={Satellite} title="Belum ada perangkat OLT terdaftar" hint='Klik "Tambah Perangkat OLT" untuk mulai' /></Card>
      ) : (
        <Table>
          <THead cols={['Nama', 'Driver', 'Host', 'Status', '']} />
          <tbody>
            {data.map(d => (
              <tr key={d.id} style={{ opacity: d.aktif ? 1 : 0.5 }}>
                <td style={{ ...td, fontWeight: 600 }}>{d.nama}</td>
                <td style={td}><Badge tone="brand">{driverLabel(d.driver)}</Badge></td>
                <td style={td}>{d.host}{d.port ? `:${d.port}` : ''}</td>
                <td style={td}><Badge tone={d.aktif ? 'success' : 'neutral'}>{d.aktif ? 'Aktif' : 'Nonaktif'}</Badge></td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <Button size="sm" variant="soft" onClick={() => bukaEdit(d)} style={{ marginRight: 6 }}>Edit</Button>
                  <Button size="sm" variant="danger-soft" onClick={() => hapus(d)}>Hapus</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {modal && (
        <Modal onClose={() => setModal(false)} width={460}>
          <ModalHeader title={editId ? 'Edit Perangkat OLT' : 'Tambah Perangkat OLT'} onClose={() => setModal(false)} />
          {error && <Alert type="error">{error}</Alert>}
          <Field label="Nama *" style={{ marginBottom: 14 }}>
            <Input value={form.nama} onChange={e => setForm({ ...form, nama: e.target.value })} placeholder="Mis. OLT Kantor Cabang A" />
          </Field>
          <Field label="Driver / Vendor *" style={{ marginBottom: 14 }}>
            <Select value={form.driver} onChange={e => setForm({ ...form, driver: e.target.value })}>
              <option value="" disabled>Pilih driver</option>
              {drivers.map(d => <option key={d.key} value={d.key}>{d.vendor} — {d.model}</option>)}
            </Select>
          </Field>
          <div className="grid-fixed-100" style={{ gap: 10, marginBottom: 14 }}>
            <Field label="Host / Alamat *">
              <Input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} placeholder="192.168.100.5 atau http://192.168.100.88" />
            </Field>
            <Field label="Port">
              <Input type="number" value={form.port} onChange={e => setForm({ ...form, port: e.target.value })} placeholder="opsional" />
            </Field>
          </div>
          <div className="grid-2" style={{ gap: 10, marginBottom: 14 }}>
            <Field label="Username">
              <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="admin" />
            </Field>
            <Field label="Password">
              <div style={{ position: 'relative' }}>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  style={{ paddingRight: 38 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', padding: 4 }}
                  aria-label={showPassword ? 'Sembunyikan password' : 'Tampilkan password'}
                >
                  {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </Field>
          </div>
          {editId && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, fontSize: 13.5, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.aktif} onChange={e => setForm({ ...form, aktif: e.target.checked })} />
              Aktif (diikutkan scan redaman & diagnosis pelanggan)
            </label>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: editId ? 0 : 22 }}>
            <Button variant="neutral" onClick={() => setModal(false)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button onClick={simpan} style={{ flex: 1, justifyContent: 'center' }}>Simpan</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
